import { useEffect, useState } from "react";
import { JSX } from "react";
import { useAdCategoryStore } from "../store/adCategoryStore";
import { useShallow } from "zustand/react/shallow";
import Dropdown from "@/components/ui/DropDown";
import useGlobalStore from "@/store/globalStore";
import Spinner from "@/components/ui/Spinner";
import { CheckCircle2, CircleDashed, Clock, X } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useAdBuilderStore from "@/pages/ad-builder/store/ad-builder-store";

/** What the saved canvas looks like, for the Template Builder indicator. */
interface SavedCanvasSummary {
  status: string;
  componentCount: number;
  updatedAt: string | null;
}

export default function CreateAdCategoriesStepFive(): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  /**
   * Held locally rather than read from `useAdBuilderStore.loadedCanvasData`.
   * That store is shared and is not cleared when `loadCanvas` finds nothing, so
   * reading it directly would show the previously viewed ad type's canvas as if
   * it belonged to this one. Setting it inside the load effect keeps it scoped
   * to the ad type actually being displayed.
   */
  const [savedCanvas, setSavedCanvas] = useState<SavedCanvasSummary | null>(
    null,
  );

  const {
    canvasBuilderData,
    selectedInterface,
    selectedAdSpecs,
    selectedAdInfoBlocks,
    fetchCanvasBuilderData,
    setSelectedInterface,
    setSelectedAdSpecs,
    setSelectedAdInfoBlocks,
    addAdSpec,
    removeAdSpec,
    addAdInfoBlock,
    removeAdInfoBlock,
  } = useAdCategoryStore(
    useShallow((state) => ({
      canvasBuilderData: state.canvasBuilderData,
      selectedInterface: state.selectedInterface,
      selectedAdSpecs: state.selectedAdSpecs,
      selectedAdInfoBlocks: state.selectedAdInfoBlocks,
      fetchCanvasBuilderData: state.fetchCanvasBuilderData,
      setSelectedInterface: state.setSelectedInterface,
      setSelectedAdSpecs: state.setSelectedAdSpecs,
      setSelectedAdInfoBlocks: state.setSelectedAdInfoBlocks,
      addAdSpec: state.addAdSpec,
      removeAdSpec: state.removeAdSpec,
      addAdInfoBlock: state.addAdInfoBlock,
      removeAdInfoBlock: state.removeAdInfoBlock,
    })),
  );

  const { loadCanvas } = useAdBuilderStore(
    useShallow((state) => ({
      loadCanvas: state.loadCanvas,
    })),
  );

  const { setError } = useGlobalStore(
    useShallow((state) => ({
      setError: state.setError,
    })),
  );

  useEffect(() => {
    const fetchData = async () => {
      const adInputId = searchParams.get("adInputId");
      if (!adInputId) return;
      try {
        setIsLoading(true);
        // Cleared up front so a failed or empty load cannot leave the previous
        // ad type's "saved" badge on screen.
        setSavedCanvas(null);
        // Reset stale store values before loading fresh Step 5 data
        useAdCategoryStore.getState().resetValue();
        await fetchCanvasBuilderData(adInputId);

        // After canvas builder data is loaded, restore previously saved step 5 selections
        const mappingId = useAdCategoryStore.getState().canvasBuilderData?.ad_category_mapping_id;
        if (mappingId) {
          let hasCanvas = true;
          await loadCanvas(mappingId).catch(() => {
            // No canvas saved yet — that's fine
            hasCanvas = false;
          });

          const loaded = useAdBuilderStore.getState().loadedCanvasData;
          const master = loaded?.canvas_master;
          const canvasInfo = master?.canvas_info_aggregated ?? {};

          if (canvasInfo.selectedInterface) setSelectedInterface(canvasInfo.selectedInterface);
          if (Array.isArray(canvasInfo.selectedAdSpecs)) setSelectedAdSpecs(canvasInfo.selectedAdSpecs);
          if (Array.isArray(canvasInfo.selectedAdInfoBlocks)) setSelectedAdInfoBlocks(canvasInfo.selectedAdInfoBlocks);

          // A canvas_master row is what makes a template "saved" — the request
          // succeeding is not enough, since it resolves with no master when
          // nothing has been built yet.
          if (hasCanvas && master?.id) {
            setSavedCanvas({
              status: master.status ?? "draft",
              componentCount: Array.isArray(loaded?.components)
                ? loaded.components.length
                : 0,
              updatedAt: master.updated_at ?? master.created_at ?? null,
            });
          }
        }
      } catch (error: any) {
        setError(error?.message || "Failed to load canvas builder data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [searchParams]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center w-screen h-screen">
        <Spinner size={30} />
      </div>
    );
  }

  if (!canvasBuilderData) {
    return (
      <div className="mt-6">
        <div className="text-center text-gray-500">
          No canvas builder data available
        </div>
      </div>
    );
  }

  const {
    ad_type,
    ad_category,
    publisher,
    interface_types,
    parameters,
    ad_info_blocks,
  } = canvasBuilderData;

  // Prepare interface options
  const interfaceOptions = (interface_types || []).map(
    (interfaceType: any) => ({
      title:
        interfaceType.name.charAt(0).toUpperCase() +
        interfaceType.name.slice(1),
      value: interfaceType.id,
    }),
  );

  // Every spec the ad type offers. Kept unfiltered so the chips below can still
  // resolve a selected key back to its label.
  const allAdSpecsOptions = (parameters || []).map((param: any) => ({
    title: param.parameter_definition.label,
    value: param.parameter_definition.key,
  }));

  // Prepare ad info blocks options - get keys from ad_info_blocks object
  const allAdInfoBlocksOptions = ad_info_blocks
    ? Object.keys(ad_info_blocks).map((key) => ({
        title: key.charAt(0).toUpperCase() + key.slice(1),
        value: key,
      }))
    : [];

  // A selected value is already shown as a chip, so drop it from the dropdown:
  // re-picking it is a no-op and only makes the list harder to scan.
  const adSpecsOptions = allAdSpecsOptions.filter(
    (opt) => !selectedAdSpecs.includes(opt.value),
  );

  const adInfoBlocksOptions = allAdInfoBlocksOptions.filter(
    (opt) => !selectedAdInfoBlocks.includes(opt.value),
  );

  const handleAdSpecSelect = (value: string) => {
    if (value && !selectedAdSpecs.includes(value)) {
      addAdSpec(value);
    }
  };

  const handleAdInfoBlockSelect = (value: string) => {
    if (value && !selectedAdInfoBlocks.includes(value)) {
      addAdInfoBlock(value);
    }
  };

  const handleCreateTemplate = () => {
    const adCategoryMappingId = canvasBuilderData?.ad_category_mapping_id;
    const path = adCategoryMappingId
      ? `/ad-builder?adCategoryMappingId=${adCategoryMappingId}`
      : "/ad-builder";
    navigate(path);
  };

  return (
    <div className="mt-6">
      <div className="font-bold text-xl mb-2">Step 5: Create Ad Template</div>
      <div className="text-sm text-gray-600 mb-4">
        <span className="font-semibold">Publisher:</span>{" "}
        {publisher?.name || "User Defined"} <span className="mx-2">|</span>
        <span className="font-semibold">Ad Type:</span>{" "}
        {ad_type?.name || "Facebook Carousel"} <span className="mx-2">|</span>
        <span className="font-semibold">Ad Parameters:</span> #
      </div>
      <hr className="border-[#EA33C0] mb-6" />

      {/* Ad Template Type */}
      <div className="mb-8">
        <h3 className="font-semibold text-base mb-3">Ad Template Type</h3>
        <div className="mb-2">
          <label className="text-sm text-gray-500 mb-1 block">Interface</label>
          <Dropdown
            options={interfaceOptions}
            selected={selectedInterface}
            setSelected={setSelectedInterface}
            placeholder="Select Option"
            className="w-64"
          />
        </div>
        {selectedInterface && (
          <div className="mt-2 text-xs text-gray-500">
            Selected:{" "}
            {interfaceOptions.find((opt) => opt.value === selectedInterface)
              ?.title || ""}
          </div>
        )}
      </div>

      {/* Ad Specs Overview */}
      <div className="mb-8">
        <h3 className="font-semibold text-base mb-1">Ad Specs Overview</h3>
        <p className="text-sm text-gray-500 mb-3">
          Select ad specs from the dropdown menu to display below the Ad Builder
          Template.
        </p>
        <Dropdown
          options={adSpecsOptions}
          selected=""
          setSelected={handleAdSpecSelect}
          placeholder={
            allAdSpecsOptions.length === 0
              ? "No specs available"
              : adSpecsOptions.length === 0
                ? "All specs added"
                : "Add Specs..."
          }
          className="w-96"
        />
        {selectedAdSpecs.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {selectedAdSpecs.map((specKey) => {
              // Resolved from the unfiltered list: the dropdown list no longer
              // contains selected values, so looking there would lose the label.
              const spec = allAdSpecsOptions.find(
                (opt) => opt.value === specKey,
              );
              return (
                <div
                  key={specKey}
                  className="inline-flex items-center gap-2 px-3 py-1 bg-gray-100 border border-gray-300 rounded-full text-sm"
                >
                  <span>{spec?.title || specKey}</span>
                  <button
                    onClick={() => removeAdSpec(specKey)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Ad Info Blocks */}
      <div className="mb-8">
        <h3 className="font-semibold text-base mb-1">Ad Info Blocks</h3>
        <p className="text-sm text-gray-500 mb-3">
          Select information blocks from the dropdown menu to display below the
          Ad Builder Template.
        </p>
        <Dropdown
          options={adInfoBlocksOptions}
          selected=""
          setSelected={handleAdInfoBlockSelect}
          placeholder={
            allAdInfoBlocksOptions.length === 0
              ? "No blocks available"
              : adInfoBlocksOptions.length === 0
                ? "All blocks added"
                : "Add Blocks ..."
          }
          className="w-96"
        />
        {selectedAdInfoBlocks.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {selectedAdInfoBlocks.map((blockKey) => {
              const block = allAdInfoBlocksOptions.find(
                (opt) => opt.value === blockKey,
              );
              return (
                <div
                  key={blockKey}
                  className="inline-flex items-center gap-2 px-3 py-1 bg-gray-100 border border-gray-300 rounded-full text-sm"
                >
                  <span>{block?.title || blockKey}</span>
                  <button
                    onClick={() => removeAdInfoBlock(blockKey)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Template Builder */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <h3 className="font-semibold text-base">Template Builder</h3>
          {/* The canvas saves through its own API, so returning here gave no
              sign of whether anything had been built. This reflects the loaded
              canvas_master. */}
          {savedCanvas ? (
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                savedCanvas.status === "completed"
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : "bg-amber-50 text-amber-700 border border-amber-200"
              }`}
              title={
                savedCanvas.updatedAt
                  ? `Last saved ${new Date(savedCanvas.updatedAt).toLocaleString()}`
                  : undefined
              }
            >
              {savedCanvas.status === "completed" ? (
                <CheckCircle2 size={14} />
              ) : (
                <Clock size={14} />
              )}
              {savedCanvas.status === "completed"
                ? "Template saved"
                : "Draft saved"}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
              <CircleDashed size={14} />
              Not created yet
            </span>
          )}
        </div>

        {savedCanvas ? (
          <div className="flex items-center gap-4">
            <button
              onClick={handleCreateTemplate}
              className="flex flex-col items-center justify-center w-40 h-40 border-2 border-green-500 rounded-lg hover:bg-green-50 transition-colors cursor-pointer"
            >
              <CheckCircle2 size={32} className="text-green-600 mb-2" />
              <div className="text-green-700 text-sm font-medium">
                Edit Template
              </div>
            </button>
            <div className="text-sm text-gray-600 space-y-1">
              <div>
                {savedCanvas.componentCount}{" "}
                {savedCanvas.componentCount === 1 ? "element" : "elements"} on
                the canvas
              </div>
              {savedCanvas.updatedAt && (
                <div className="text-xs text-gray-500">
                  Last saved{" "}
                  {new Date(savedCanvas.updatedAt).toLocaleString()}
                </div>
              )}
            </div>
          </div>
        ) : (
          <button
            onClick={handleCreateTemplate}
            className="flex flex-col items-center justify-center w-40 h-40 border-2 border-pink-500 rounded-lg hover:bg-pink-50 transition-colors cursor-pointer"
          >
            <div className="text-pink-500 text-4xl mb-2">+</div>
            <div className="text-pink-500 text-sm font-medium">
              Create Template
            </div>
          </button>
        )}
      </div>
    </div>
  );
}
